/**
 * pd-wishlist.js — saved products.
 *
 * The list lives in OUR database, not the browser, because the shop needs to see what is being
 * saved and by whom. See web/app/fitment/wishlist.server.js.
 *
 * Identity:
 *   anonId     a random id in localStorage, created once, so a guest can save before logging in
 *   customerId rendered by Liquid when Shopify has authenticated the session
 *
 * When a logged-in customer shows up with a guest list, we ask the server to merge the two
 * ONCE per browser, then stop. Re-merging on every page load would quietly resurrect items the
 * customer had deliberately removed from their account list.
 */
(function () {
  "use strict";

  var ANON_KEY = "pd_wish_anon";
  var MERGED_KEY = "pd_wish_merged"; // which customer this browser has already merged into

  function ctx() {
    var el = document.querySelector("[data-pd-ctx]");
    return {
      shop: el ? el.dataset.shop : null,
      customerId: el && el.dataset.customer ? el.dataset.customer : null,
      base: (el && el.dataset.apiBase) || "https://pd-garage.fly.dev",
      strings: {
        add: (el && el.dataset.wAdd) || "Add to wishlist",
        added: (el && el.dataset.wAdded) || "Saved",
        addCart: (el && el.dataset.wCart) || "Add to cart",
        sold: (el && el.dataset.wSold) || "Sold out",
        gone: (el && el.dataset.wGone) || "No longer available",
        empty: (el && el.dataset.wEmpty) || "Nothing saved yet.",
        browse: (el && el.dataset.wBrowse) || "Browse parts",
        title: (el && el.dataset.wTitle) || "My wishlist",
      },
      card: cardStrings(),
    };
  }

  // The feed card's own strings, from the wishlist page container. They are the theme's
  // pd.card.* keys, so a saved product reads exactly like the same product in the feed.
  // Read again on every render: a page swapped in by the tab bar (pd-nav.js) was not on screen
  // when this script started, and the cards came out in the English fallbacks.
  function cardStrings() {
    var w = document.querySelector("[data-pd-wishpage]");
    return {
      add: (w && w.dataset.cAdd) || "Add to cart",
      inStock: (w && w.dataset.cIn) || "In stock",
      outStock: (w && w.dataset.cOut) || "Out of stock",
      qty: (w && w.dataset.cQty) || "Quantity",
      outlet: (w && w.dataset.cOutlet) || "Outlet",
      save: (w && w.dataset.cSave) || "save",
    };
  }

  function anonId() {
    try {
      var v = localStorage.getItem(ANON_KEY);
      if (!v) {
        v = "a" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(ANON_KEY, v);
      }
      return v;
    } catch (e) {
      // private mode: a per-page id still lets the heart work, it just will not persist
      return "a-ephemeral";
    }
  }

  var C = ctx();
  var ANON = anonId();
  var ids = new Set();

  function api(path, params) {
    var base = C.base.replace(/\/$/, "");
    var url = new URL("/api/storefront/wishlist" + (path || ""), base);
    Object.keys(params || {}).forEach(function (k) {
      if (params[k] !== undefined && params[k] !== null && params[k] !== "") url.searchParams.set(k, params[k]);
    });
    return fetch(url.toString(), { headers: { Accept: "application/json" } }).then(function (r) {
      if (!r.ok) throw new Error("wishlist " + r.status);
      return r.json();
    });
  }

  function post(body) {
    return fetch(C.base.replace(/\/$/, "") + "/api/storefront/wishlist", {
      method: "POST",
      // text/plain is CORS-safelisted, so this POST needs NO preflight. With
      // application/json the browser sends OPTIONS first, Remix routed that to the loader,
      // the loader answered 400 "shop is required" (a preflight carries no query string) and
      // the POST was dropped — the heart lit up and reverted half a second later.
      // The body is still JSON; Request.json() parses it regardless of content type.
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body: JSON.stringify(Object.assign({ shop: C.shop, anonId: ANON, customerId: C.customerId }, body)),
    }).then(function (r) {
      if (!r.ok) throw new Error("wishlist " + r.status);
      return r.json();
    });
  }

  /** Merge a guest list into the account, once per browser per customer. */
  async function mergeOnce() {
    if (!C.customerId) return null;
    try {
      if (localStorage.getItem(MERGED_KEY) === C.customerId) return null;
    } catch (e) {
      /* no storage: skip the merge rather than repeat it every load */
      return null;
    }
    var res = await post({ action: "merge" });
    try {
      localStorage.setItem(MERGED_KEY, C.customerId);
    } catch (e) {}
    return res;
  }

  function paint() {
    document.querySelectorAll("[data-wish-toggle]").forEach(function (el) {
      var on = ids.has(String(el.dataset.productId));
      el.classList.toggle("is-on", on);
      el.setAttribute("aria-pressed", on ? "true" : "false");
      var label = el.querySelector("[data-wish-label]");
      if (label) label.textContent = on ? C.strings.added : C.strings.add;
      // The two hearts are both in the DOM; CSS shows one. Swapping innerHTML from a data-
      // attribute is what produced the literal string "undefined" on the button.
      if (el.dataset.labelAdd) el.setAttribute("aria-label", on ? el.dataset.labelOn || C.strings.added : el.dataset.labelAdd);
    });
    document.querySelectorAll("[data-wish-count]").forEach(function (el) {
      el.textContent = (el.hasAttribute("data-wish-count-plain") ? "" : "+") + ids.size;   // the tab bar badge is a plain count, like the cart's
      el.hidden = ids.size === 0;
    });
    // Deliberately nothing here for the header heart: it stays an outline whatever the list
    // holds. The only time it fills is the moment a heart lands on it, and that fades out again.
  }

  async function refresh() {
    if (!C.shop) return;
    try {
      var merged = await mergeOnce();
      var res = merged && merged.ids ? merged : await api("", { shop: C.shop, anonId: ANON, customerId: C.customerId, mode: "ids" });
      ids = new Set((res.ids || []).map(String));
      paint();
    } catch (e) {
      /* the heart simply stays unlit */
    }
  }

  async function toggle(el) {
    var pid = String(el.dataset.productId || "");
    if (!pid || el.dataset.busy) return;
    el.dataset.busy = "1";
    // Paint first: a heart that waits for the network feels broken. If the call fails we put
    // it back.
    var wanted = !ids.has(pid);
    if (wanted) ids.add(pid); else ids.delete(pid);
    paint();
    try {
      var res = await post({
        productId: pid, sku: el.dataset.sku || null,
        variantId: el.dataset.variantId || null, handle: el.dataset.handle || null,
        action: wanted ? "add" : "remove",
      });
      if (res.saved) ids.add(pid); else ids.delete(pid);
      if (res.saved && wanted && el.classList.contains("pd-wish-heart-btn")) flyToHeader(el);
    } catch (e) {
      if (wanted) ids.delete(pid); else ids.add(pid);
    } finally {
      delete el.dataset.busy;
      paint();
      document.dispatchEvent(new CustomEvent("pd:wishlist-changed", { detail: { count: ids.size } }));
    }
  }

  // --- market-correct pricing -----------------------------------------------------------------
  //
  // products_cache holds the shop's base DKK amount. Formatting that with the page's currency
  // prints "EUR 3,169" for a EUR 432 part. Same @inContext approach as the fairing kit and the
  // feed; the cached amount is only the fallback.

  var SF_QUERY =
    "query P($ids:[ID!]!,$country:CountryCode!) @inContext(country:$country){ nodes(ids:$ids){ ... on ProductVariant { id price{ amount currencyCode } compareAtPrice{ amount currencyCode } } } }";

  /**
   * The public Storefront token. It comes with the wishlist payload; until the app is next
   * deployed that route does not send one, so fall back to an endpoint that already does.
   * Once the deploy lands the fallback stops being used and can be deleted.
   */
  async function storefrontToken(data) {
    if (data && data.storefrontToken) return { token: data.storefrontToken, version: data.storefrontApiVersion || "2025-01" };
    try {
      var base = C.base.replace(/\/$/, "");
      var r = await fetch(base + "/api/storefront/fairing-kit?shop=" + encodeURIComponent(C.shop) + "&mode=skus");
      if (!r.ok) return null;
      var j = await r.json();
      return j.storefrontToken ? { token: j.storefrontToken, version: j.storefrontApiVersion || "2025-01" } : null;
    } catch (e) {
      return null;
    }
  }

  async function priceInMarket(data) {
    var country = (document.querySelector("[data-pd-ctx]") || {}).dataset
      ? document.querySelector("[data-pd-ctx]").dataset.country
      : null;
    var rows = (data.items || []).filter(function (i) { return i.variantId; });
    if (!rows.length || !country) return null;
    var t = await storefrontToken(data);
    if (!t) return null;
    var ids = [...new Set(rows.map(function (i) { return i.variantId; }))];
    var market = new Map();
    try {
      for (var i = 0; i < ids.length; i += 200) {
        var res = await fetch("https://" + C.shop + "/api/" + t.version + "/graphql.json", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": t.token },
          body: JSON.stringify({
            query: SF_QUERY,
            variables: { ids: ids.slice(i, i + 200).map(function (v) { return "gid://shopify/ProductVariant/" + v; }), country: country },
          }),
        });
        if (!res.ok) continue;
        var body = await res.json();
        ((body && body.data && body.data.nodes) || []).forEach(function (n) {
          if (!n || !n.id || !n.price) return;
          var m = String(n.id).match(/(\d+)$/);
          if (m) market.set(m[1], {
            amount: Number(n.price.amount),
            currency: n.price.currencyCode,
            compareAt: n.compareAtPrice && n.compareAtPrice.amount != null ? Number(n.compareAtPrice.amount) : null,
          });
        });
      }
    } catch (e) {
      return null;
    }
    if (!market.size) return null;
    var ccy = null;
    rows.forEach(function (r) {
      var mk = market.get(String(r.variantId));
      if (!mk || mk.amount == null) return;
      r.price = mk.amount;
      r.compareAt = mk.compareAt;
      ccy = ccy || mk.currency;
    });
    return ccy;
  }

  // --- the wishlist page --------------------------------------------------------------------

  var MARKET_CCY = null; // set once the Storefront prices come back
  function money(a) {
    var n = Number(a);
    if (!isFinite(n)) return "";
    if (window.pdFormatMoney && (!MARKET_CCY || MARKET_CCY === window.pdMoneyCcy)) return window.pdFormatMoney(n);   // the product page's format (theme.liquid)
    var ctxEl = document.querySelector("[data-pd-ctx]");
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: MARKET_CCY || (ctxEl && ctxEl.dataset.currency) || "DKK",
      }).format(n);
    } catch (e) { return String(a); }
  }
  // Shopify CDN images at card size: the stored URL is the original file (up to 2048px for a ~200px card).
  function cardImg(u) { if (!u || (u.indexOf("cdn.shopify.com") < 0 && u.indexOf("/cdn/shop/") < 0)) return u; return u + (u.indexOf("?") >= 0 ? "&" : "?") + "width=480"; }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  async function renderPage(root) {
    var grid = root.querySelector("[data-wish-grid]");
    if (!grid) return;
    C.card = cardStrings();
    var data;
    try {
      await mergeOnce();
      data = await api("", { shop: C.shop, anonId: ANON, customerId: C.customerId });
    } catch (e) {
      return;
    }
    // Price in the visitor's market BEFORE rendering, so no card ever shows the base amount
    // with the wrong symbol, not even for a frame.
    MARKET_CCY = (await priceInMarket(data)) || MARKET_CCY;
    ids = new Set(data.items.map(function (i) { return String(i.productId); }));
    var countEl = root.querySelector("[data-wish-total]");
    if (countEl) countEl.textContent = String(data.count);

    if (!data.items.length) {
      grid.outerHTML =
        '<div class="pd-wishpage__empty" data-wish-grid>' + esc(C.strings.empty) +
        ' <a href="/collections/mc-parts">' + esc(C.strings.browse) + "</a></div>";
      return;
    }

    // The product feed's card, built with the same class names so pd-feed-cards.css styles it
    // identically. Do not restyle it here — if the feed card changes, this should change with it.
    grid.innerHTML = data.items
      .map(function (i) {
        var href = i.handle ? "/products/" + i.handle : null;
        var name = i.title || i.sku || i.productId;
        var buyable = i.available && i.variantId && !i.gone;
        var off = i.compareAt && i.compareAt > i.price ? Math.round((1 - i.price / i.compareAt) * 100) : 0;
        var open = href ? '<a href="' + esc(href) + '">' : "<span>";
        var close = href ? "</a>" : "</span>";
        return (
          '<li class="pd-prow__item">' +
          '<article class="pd-card pd-card--clean">' +
          '<button type="button" class="pd-wish-heart is-on" data-wish-toggle data-product-id="' + esc(i.productId) +
          '" data-sku="' + esc(i.sku || "") + '" aria-label="' + esc(C.strings.add) + '">' + HEART_ON + "</button>" +
          (href ? '<a href="' + esc(href) + '" class="pd-thumb">' : '<span class="pd-thumb">') +
          '<div class="pd-thumb__art">' +
          (i.image ? '<img src="' + esc(cardImg(i.image)) + '" alt="' + esc(name) + '" loading="lazy">' : '<div class="pd-thumb__art--placeholder"></div>') +
          "</div>" +
          '<div class="pd-overlays"><span></span>' + (off > 0 ? '<span class="pd-sale">-' + off + "%</span>" : "<span></span>") + "</div>" +
          (href ? "</a>" : "</span>") +
          '<div class="pd-card__body">' +
          open + '<h3 class="pd-name">' + esc(name) + "</h3>" + close +
          (i.sku ? '<span class="pd-sku">' + esc(i.sku) + "</span>" : '<span class="pd-sku"></span>') +
          '<span class="pd-stock ' + (buyable ? "pd-stock--in" : "pd-stock--out") + '"><i></i>' +
          esc(i.gone ? C.strings.gone : buyable ? C.card.inStock : C.card.outStock) + "</span>" +
          '<div class="pd-card__foot"><div class="pd-price">' +
          (off > 0 ? "<s>" + esc(money(i.compareAt)) + "</s>" : "") +
          "<strong>" + esc(money(i.price)) + "</strong></div></div>" +
          '<div class="pd-qty" data-pd-qty>' +
          '<button type="button" class="pd-qty__btn" data-pd-qty-dec aria-label="−">−</button>' +
          '<input class="pd-qty__in" type="number" inputmode="numeric" min="1" max="99" value="1" aria-label="' + esc(C.card.qty) + '">' +
          '<button type="button" class="pd-qty__btn" data-pd-qty-inc aria-label="+">+</button></div>' +
          '<button type="button" class="pd-add pd-add--block" data-wish-add data-variant-id="' + esc(i.variantId || "") + '"' +
          (buyable ? "" : " disabled") + "><span>" + esc(C.card.add) + "</span></button>" +
          "</div></article></li>"
        );
      })
      .join("");
    paint();
  }

  var HEART_OFF = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20.2 4.6 13a4.6 4.6 0 0 1 6.5-6.5l.9.9.9-.9A4.6 4.6 0 0 1 19.4 13z"/></svg>';
  var HEART_ON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 20.8 4.1 13.4a5.3 5.3 0 0 1 7.4-7.6l.5.5.5-.5a5.3 5.3 0 0 1 7.4 7.6z"/></svg>';

  /**
   * The section renders at the bottom of the product template; move the button into the buy
   * zone, under Add to cart. Left where it is rather than thrown away if the buy zone cannot
   * be found — a button in the wrong place still works, a missing one does not.
   */
  function placeButton() {
    var slot = document.querySelector("[data-pd-wish-slot]");
    if (!slot) return;
    // Into the theme's own buy row, to the LEFT of the quantity picker, so the row reads
    // heart / quantity / add. The row is a 10px-gap flex, so the slot just becomes an item.
    var row = document.querySelector(".product-form__buttons");
    var qty = row && row.querySelector(".product-form__quantity");
    if (row) {
      row.insertBefore(slot, qty || row.firstChild);
      row.classList.add("pd-has-wish");
    } else {
      var host = document.querySelector(".product__block--buy_buttons") ||
        document.querySelector('form[action*="/cart/add"]');
      if (host && host.parentNode) host.parentNode.insertBefore(slot, host.nextSibling);
    }
    slot.hidden = false;
  }

  // 28px, the same heart as the product page, so the two read as one control in two places.
  // There is only one shape: the header heart never changes. The flying heart lands on top of
  // it and fades out there — that is the whole effect.
  var HDR_OUTLINE =
    '<span class="pd-wish-hdr__ico pd-wish-hdr__ico--off"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M12 20.2 4.6 13a4.6 4.6 0 0 1 6.5-6.5l.9.9.9-.9A4.6 4.6 0 0 1 19.4 13z"/></svg></span>';
    "</svg></span>";

  /**
   * A heart in the header, the same circle as the cart, first in the button row so it sits
   * between the search field and the garage. Injected rather than added to the theme's header
   * section: that section is the theme's own and gets replaced whenever Hyper updates.
   */
  function mountHeaderHeart() {
    // There are TWO .header__buttons rows — a hidden one in .header__icons--left and the real
    // one on the right. Find the row by the CART, which is only ever in the visible one;
    // querySelector(".header__buttons") picks the hidden one and the heart never appears.
    var cart = document.querySelector(".cart-drawer-button, [href$='/cart']");
    var row = (cart && cart.closest(".header__buttons")) ||
      [].slice.call(document.querySelectorAll(".header__buttons")).filter(function (r) {
        return r.offsetParent !== null;
      })[0];
    if (!row || row.querySelector("[data-wish-header]")) return;
    var a = document.createElement("a");
    a.className = "pd-wish-hdr";
    a.href = "/pages/wishlist";
    a.setAttribute("data-wish-header", "");
    a.setAttribute("aria-label", C.strings.title);
    // No count badge, by request. The header heart itself carries the state instead: outline
    // when nothing is saved, solid red once something is — otherwise, with the badge gone,
    // nothing in the header would show that the list has anything in it.
    a.innerHTML = HDR_OUTLINE;
    // Immediately to the LEFT of the cart. Anchoring on the cart rather than on the start of
    // the row also sidesteps the garage, which mounts itself into this header after us and
    // takes first place.
    row.insertBefore(a, cart && cart.parentElement === row ? cart : null);
  }

  /**
   * The saved heart flies from the product button up to the header, then the badge bumps.
   *
   * Purely decorative, and deliberately fire-and-forget: it runs on a clone in a fixed layer,
   * so nothing in the page layout moves, and if the header heart is not on screen we simply
   * skip it rather than animate into nowhere.
   */
  function flyToHeader(from) {
    var target = document.querySelector("[data-wish-header]");
    if (!from || !target || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    var a = from.getBoundingClientRect();
    var b = target.getBoundingClientRect();
    if (!a.width || !b.width) return;

    var ghost = document.createElement("span");
    ghost.className = "pd-wish-fly";
    ghost.innerHTML =
      // 28px: the same size as the header heart it lands on, so it covers it exactly
      '<svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="none">' +
      '<path d="M12 20.8 4.1 13.4a5.3 5.3 0 0 1 7.4-7.6l.5.5.5-.5a5.3 5.3 0 0 1 7.4 7.6z"/></svg>';
    ghost.style.left = a.left + a.width / 2 + "px";
    ghost.style.top = a.top + a.height / 2 + "px";
    document.body.appendChild(ghost);

    var dx = b.left + b.width / 2 - (a.left + a.width / 2);
    var dy = b.top + b.height / 2 - (a.top + a.height / 2);

    /**
     * One continuous curve, sampled densely — not a handful of keyframes with an easing each.
     *
     * The previous version had eight keyframes, each with its own cubic-bezier. Every segment
     * eased in and out separately, so the velocity dropped to near zero at each one and the
     * whole thing stuttered. With a sampled curve the shape comes from the maths and every
     * keyframe is linear, so there is nothing left to stutter against.
     *
     *   position  a cubic Bézier whose control points bend the path right, then left, then up
     *   rotation  15·sin(2πt): 0 → +15° → 0 → −15° → 0, continuous, no corners
     *   scale     grows into the lift and settles at exactly 1 on arrival
     */
    var SWAY = 120;   // how far the control points push the curve sideways
    var LIFT = 190;   // how far above the straight line the curve rises

    var p1x = dx * 0.15 + SWAY, p1y = dy * 0.15 - LIFT;
    var p2x = dx * 0.55 - SWAY * 1.5, p2y = dy * 0.6 - LIFT * 0.85;

    function bez(a0, a1, a2, a3, t) {
      var u = 1 - t;
      return u * u * u * a0 + 3 * u * u * t * a1 + 3 * u * t * t * a2 + t * t * t * a3;
    }
    // one gentle acceleration and one gentle stop across the WHOLE flight, applied to the
    // curve parameter rather than to each segment
    function easeInOutSine(t) { return -(Math.cos(Math.PI * t) - 1) / 2; }

    // Of the total duration: fly, hold still, then fade. The fade gets nearly a third of the
    // time on purpose — at 16% it was over in ~280ms and read as the heart vanishing rather
    // than settling into the header.
    var TRAVEL = 0.62;
    var HOLD = 0.70;
    var SAMPLES = 60;
    var frames = [];
    for (var i = 0; i <= SAMPLES; i++) {
      var raw = i / SAMPLES;
      var t = easeInOutSine(raw);
      var x = bez(0, p1x, p2x, dx, t);
      var y = bez(0, p1y, p2y, dy, t);
      var rot = 15 * Math.sin(raw * Math.PI * 2);
      var scale = 0.62 + 0.38 * t + 0.2 * Math.sin(Math.PI * raw);
      frames.push({
        offset: raw * TRAVEL,
        transform: "translate(calc(-50% + " + x.toFixed(2) + "px), calc(-50% + " + y.toFixed(2) + "px)) rotate(" + rot.toFixed(2) + "deg) scale(" + scale.toFixed(3) + ")",
        opacity: 1,
        easing: "linear",
      });
    }
    var landed = "translate(calc(-50% + " + dx + "px), calc(-50% + " + dy + "px)) rotate(0deg) scale(1)";
    // arrived whole: hold still for a beat, then let go
    frames.push({ offset: HOLD, transform: landed, opacity: 1, easing: "linear" });
    // shrinks towards the header heart as it goes, so it dissolves INTO it rather than over it
    frames.push({ offset: 1, transform: landed.replace("scale(1)", "scale(0.62)"), opacity: 0, easing: "cubic-bezier(0.33, 0, 0.67, 1)" });

    var anim = ghost.animate(frames, { duration: 1650, easing: "linear" });
    anim.onfinish = function () {
      ghost.remove();
      // One small beat on the header heart as the ghost settles into it. Scale only — the red
      // belongs to the heart that flew up, not to this one.
      target.classList.remove("is-bump");
      void target.offsetWidth; // restart it if two land in a row
      target.classList.add("is-bump");
      target.addEventListener(
        "animationend",
        function () { target.classList.remove("is-bump"); },
        { once: true },
      );
    };
  }

  // The wishlist gate (layout/theme.liquid, phones only): the page waits behind the circle until the list
  // is drawn, then fades in once — it used to arrive empty and the saved products popped in after it
  // (merchant 2026-09-24). Called on every outcome (list, empty, API down, no shop), never left hanging;
  // `after` keeps a hard load's circle up long enough not to flash (the tab bar's swap has had its circle).
  function releaseGate(after) {
    var style = document.querySelector('style[data-pd-gate="wishlist"]');
    if (!style) return;
    var spin = document.querySelector("[data-pd-gate-spinner]");
    setTimeout(function () {
      if (!style.isConnected) return;   // the 8 s safety (layout / pd-nav.js) already let it go
      if (spin) spin.classList.add("is-done");
      setTimeout(function () {
        if (!style.isConnected) return;
        style.remove();   // also lets the footer back in: the gate style hides it (its .45s transition fades it)
        if (spin) spin.remove();
        var secs = Array.prototype.slice.call(document.querySelectorAll("#MainContent > .shopify-section"));
        secs.forEach(function (el) { el.classList.add("pd-fadein"); });
        setTimeout(function () { secs.forEach(function (el) { el.classList.remove("pd-fadein"); }); }, 700);
      }, 180);
    }, Math.max(0, after || 0));
  }
  function showPage(page, since) {
    var t0 = Date.now();
    renderPage(page).catch(function () {}).then(function () { releaseGate(since ? since - (Date.now() - t0) : 0); });
  }

  function init() {
    if (!C.shop) { releaseGate(0); return; }
    if (document.documentElement.dataset.pdWishReady) return;
    document.documentElement.dataset.pdWishReady = "1";

    document.addEventListener("click", function (ev) {
      var t = ev.target.closest("[data-wish-toggle]");
      if (t) { ev.preventDefault(); return toggle(t); }
      var step = ev.target.closest("[data-pd-qty-dec], [data-pd-qty-inc]");
      if (step) {
        var box = step.closest("[data-pd-qty]");
        var inp = box && box.querySelector(".pd-qty__in");
        if (inp) {
          var n = parseInt(inp.value, 10) || 1;
          inp.value = step.hasAttribute("data-pd-qty-inc") ? Math.min(99, n + 1) : Math.max(1, n - 1);
        }
        return;
      }
      var add = ev.target.closest("[data-wish-add]");
      if (add && add.dataset.variantId) {
        var card = add.closest(".pd-card");
        var q = card && card.querySelector(".pd-qty__in");
        add.disabled = true;
        fetch("/cart/add.js", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: Number(add.dataset.variantId), quantity: parseInt(q && q.value, 10) || 1 }] }),
        }).finally(function () {
          add.disabled = false;
          document.dispatchEvent(new CustomEvent("pd:cart-updated"));
        });
      }
    });

    mountHeaderHeart();
    placeButton();
    var page = document.querySelector("[data-pd-wishpage]");
    if (page) showPage(page, 350);   // the same minimum as the product gate (pd-product.js)
    else { refresh(); releaseGate(0); }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
  // The page transition (pd-nav.js) swaps #MainContent in place — the wishlist page arrives that way
  // from the tab bar (2026-09-24), and its grid is drawn by renderPage(), which init() only ran on load.
  document.addEventListener("pd:navigated", function () {
    if (!C.shop) { releaseGate(0); return; }
    var page = document.querySelector("[data-pd-wishpage]");
    if (page) showPage(page, 0); else { refresh(); releaseGate(0); }
  });
})();
